import { zodResolver } from "@hookform/resolvers/zod";
import { LoaderPinwheel, Send } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { useSendMessage } from "@/api/chat/send-message";
import { Button } from "@/components/ui/button";
import AnimatedChatBubble from "@/components/ui/animated-chat-bubble";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Form, FormField } from "@/components/ui/form";
import useMessageData from "@/store/message-data";
import useOnboardingData from "@/store/onboarding-data";
import { useGetWidgetSettings } from "@/api/chat-settings/get-widget-settings";

enum ChatMessageFrom {
    BOT = "bot",
    USER = "user",
}

const formSchema = z.object({
    message: z.string({
        required_error: "Message cannot be empty",
    }),
});

const TestChatDialog = () => {
    const form = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            message: "",
        },
    });

    const { messages, setMessages, setSessionId, sessionId } = useMessageData();

    const { mutate: sendMessage, data: recievedMsg, isPending: isSending } = useSendMessage();
    const { storeData } = useOnboardingData();
    const { data: widgetSettings } = useGetWidgetSettings();

    const [welcomeMessageSet, setWelcomeMessageSet] = useState(false);

    useEffect(() => {
        if (widgetSettings && messages.length === 0 && !welcomeMessageSet) {
            console.log({ widgetSettings });
            const welcomeMessage = widgetSettings?.data?.look_and_feel?.welcome_message ??
                "Hi, I am Shoe Einstein. An AI-based chat assistant to help you with your query. I am available 24/7 for your assistance.";
            setMessages([
                {
                    message: welcomeMessage,
                    from: "bot",
                },
            ]);
            setWelcomeMessageSet(true);
        }
    }, [widgetSettings, messages.length, welcomeMessageSet]);

    useEffect(() => {
        if (recievedMsg) {
            // Add the main response message
            const mainResponseMessage = {
                message: recievedMsg.data.response,
                from: ChatMessageFrom.BOT,
                products: recievedMsg?.data?.products,
                categories: recievedMsg?.data?.categories,
            };
            
            // Add both messages to the chat
            const newMessages = [
                ...messages,
                mainResponseMessage,
            ];
            
            // Check if there's a proactive response and add it with proactive properties
            if (recievedMsg.data.proactive_response) {
                const proactiveResponseMessage = {
                    message: recievedMsg.data.proactive_response,
                    from: ChatMessageFrom.BOT,
                    isProactive: true,  // Mark as proactive
                    proactiveDelay: 3000,  // 3 second delay (can be adjusted)
                    // No products/categories for proactive responses
                };
                
                newMessages.push(proactiveResponseMessage);
            }
            
            setMessages(newMessages);
            setSessionId(recievedMsg.data.session_id);
            scrollToTarget();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [recievedMsg]);

    const onSubmit = (values: z.infer<typeof formSchema>) => {
        const newMessages = [...messages, { 
            message: values.message, 
            from: ChatMessageFrom.USER,
        }];
        setMessages(newMessages);
        sendMessage({
            message: values.message,
            storeId: storeData?.id,
            session_id: sessionId || undefined,
        });
        scrollToTarget();
        form.reset();
    };

    const targetRef = useRef<HTMLLIElement>(null);

    const scrollToTarget = () => {
        if (targetRef.current) {
            targetRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    };

    return (
        <div>
            <Dialog>
                <DialogTrigger className="w-full rounded-md bg-primary p-2 text-sm text-white md:w-auto">Test Chat</DialogTrigger>
                <DialogContent title="" className="min-h-[450px] border-none p-0 text-white">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 rounded-t-md bg-blue-500 p-4 text-white">
                            <p className="mr-2">{widgetSettings?.data?.look_and_feel?.name ?? "Shoe AI Chatbot"}</p>
                            <div className="size-2 rounded-full bg-green-600"></div>
                            <p className="text-xs">Active</p>
                        </DialogTitle>
                        <DialogDescription className="p-4">
                            <div className="h-72 overflow-y-auto scroll-smooth">
                                <div className="w-full p-4">
                                    <ul className="space-y-3">
                                        {messages && Array.isArray(messages) ? messages.map((message, index) => (
                                            <li key={`${index}-${message.message?.substring(0, 10) || index}`}>
                                                <AnimatedChatBubble
                                                    messageFrom={message.from as ChatMessageFrom}
                                                    orientation={message.from === "bot" ? "left" : "right"}
                                                    products={message.products}
                                                    categories={message.categories}
                                                    animationPlayed={true} // No animation, show immediately
                                                    isProactive={message.isProactive}
                                                    proactiveDelay={message.proactiveDelay || 1500}
                                                    // No animation completion handler needed
                                                >
                                                    {message.message}
                                                </AnimatedChatBubble>
                                            </li>
                                        )) : null}
                                        {isSending && (
                                            <li className="flex items-center justify-center gap-4">
                                                <LoaderPinwheel className="animate-spin" /> <p>Thinking...</p>
                                            </li>
                                        )}
                                        <li ref={targetRef} className="h-10"></li>
                                    </ul>
                                </div>
                            </div>
                            <Form {...form}>
                                <form onSubmit={form.handleSubmit(onSubmit)} className="grid grid-cols-2 gap-4">
                                    <div className="col-span-2">
                                        <div className="absolute bottom-0 left-0 w-full rounded-b-xl border bg-white p-4">
                                            <FormField
                                                control={form.control}
                                                name="message"
                                                render={({ field }) => (
                                                    <input
                                                        autoComplete="off"
                                                        {...field}
                                                        autoFocus={true}
                                                        type="text"
                                                        placeholder="Type a message..."
                                                        className="h-12 w-full rounded-xl border border-none bg-blue-100 px-4 pr-14 outline-none"
                                                    />
                                                )}
                                            />
                                            <Button
                                                type="submit"
                                                className="absolute right-6 top-1/2 -translate-y-1/2 rounded-full bg-blue-500 p-2 font-semibold text-white"
                                            >
                                                <Send className="size-4" />
                                            </Button>
                                        </div>
                                    </div>
                                </form>
                            </Form>
                        </DialogDescription>
                    </DialogHeader>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default TestChatDialog;
